import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("orbit_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      console.error("Auth error:", err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, property_id = null) => {
    const res = await axios.post(`${API}/auth/login`, { username, password, property_id });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("orbit_token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("orbit_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const canAcknowledge = () => ["HOD", "DM", "GM"].includes(user?.role);
  const canResolve = () => ["HOD", "DM", "GM"].includes(user?.role);
  const canClose = (issue) => {
    if (!user) return false;
    if (user.role === "GM" || user.role === "DM") return true;
    if (user.role === "HOD") {
      return issue?.final_severity !== "S4" && issue?.guest_type !== "VIP";
    }
    return false;
  };
  const canReopen = () => user?.role === "GM";
  const canOverrideSLA = () => user?.role === "GM";
  const canEditSeverity = () => ["DM", "GM"].includes(user?.role);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      canAcknowledge,
      canResolve,
      canClose,
      canReopen,
      canOverrideSLA,
      canEditSeverity,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
